import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import "./dashboard.css";
import "./classesAttendance.css";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faUsers,
  faSearch,
  faFilter,
  faRefresh,
  faUserPlus,
  faUserMinus,
  faBookOpen,
  faArrowLeft,
  faCheckCircle,
  faClipboardList,
  faChartLine,
  faSpinner,
  faExclamationTriangle,
  faCalendarDay,
  faCalendarAlt,
  faDatabase,
  faBug,
  faTimes,
  faEye,
  faUserClock,
  faCalendarTimes,
  faClipboardCheck,
  faCheck,
  faTrash,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import ReactDOM from "react-dom";

// PHP API Base URL
const PHP_API_BASE = "https://mhsdev.online/MHS-WEB/api/ManageAttendance.php";

const ClassAttendanceCK = () => {
  const [filter, setFilter] = useState("All Students");
  const [sectionFilter, setSectionFilter] = useState("All");
  const [students, setStudents] = useState([]);
  const [sections, setSections] = useState({});
  const [availableSections, setAvailableSections] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedStudentsData, setSelectedStudentsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [attendanceDate, setAttendanceDate] = useState("");
  const [attendanceDay, setAttendanceDay] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [absentToday, setAbsentToday] = useState(0);
  const [absentTodayList, setAbsentTodayList] = useState([]);
  const [isLoadingAbsentToday, setIsLoadingAbsentToday] = useState(false);
  const [showAbsentModal, setShowAbsentModal] = useState(false);
  const [markingPresent, setMarkingPresent] = useState({});
  const [modalLoading, setModalLoading] = useState(false);

  // Toggle for showing/hiding details (stats & filters) – default hidden
  const [showDetails, setShowDetails] = useState(false);

  const suggestionsRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const classId = location.state?.className;

  // Stats state with real-time calculation
  const [apiStats, setApiStats] = useState({
    totalStudents: 0,
    selectedCount: 0,
    sectionCount: 0,
    attendanceRate: "0%",
    absentToday: 0,
  });

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Initialize date and day
  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toISOString().split("T")[0];
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayName = days[today.getDay()];

    setAttendanceDate(formattedDate);
    setAttendanceDay(dayName);
  }, []);

  // Function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Function to get day name from date
  const getDayFromDate = (dateString) => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const date = new Date(dateString);
    return days[date.getDay()];
  };

  // Function to calculate real-time attendance rate
  const calculateAttendanceRate = (total, absent) => {
    if (total === 0) return "0%";
    const present = total - absent;
    const rate = Math.round((present / total) * 100);
    return `${rate}%`;
  };

  // Fetch today's absent students
  const fetchAbsentToday = async () => {
    if (!classId) return;

    try {
      setIsLoadingAbsentToday(true);
      const todayDate = getTodayDate();
      
      const response = await fetch(
        `${PHP_API_BASE}?action=get-absent-records-by-date&date=${todayDate}&class=${encodeURIComponent(
          classId
        )}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setAbsentTodayList(data.data || []);
        const absentCount = data.count || 0;
        setAbsentToday(absentCount);
        
        // Update stats with real-time calculation
        setApiStats(prev => ({
          ...prev,
          absentToday: absentCount,
          attendanceRate: calculateAttendanceRate(prev.totalStudents, absentCount)
        }));
      } else {
        setAbsentTodayList([]);
        setAbsentToday(0);
        setApiStats(prev => ({
          ...prev,
          absentToday: 0,
          attendanceRate: calculateAttendanceRate(prev.totalStudents, 0)
        }));
      }
    } catch (error) {
      console.error("Error fetching today's absent:", error);
      setAbsentTodayList([]);
      setAbsentToday(0);
    } finally {
      setIsLoadingAbsentToday(false);
    }
  };

  // Stats array with real-time calculation
  const stats = [
    {
      label: "TOTAL STUDENTS",
      value: apiStats.totalStudents,
      icon: faUsers,
      color: "#667eea",
      type: "total",
    },
    {
      label: "SELECTED",
      value: apiStats.selectedCount,
      icon: faCheckCircle,
      color: "#48bb78",
      type: "selected",
    },
    {
      label: "SECTIONS",
      value: apiStats.sectionCount,
      color: "#ed8936",
      icon: faClipboardList,
      type: "sections",
    },
    {
      label: "ABSENT TODAY",
      value: isLoadingAbsentToday ? (
        <FontAwesomeIcon icon={faSpinner} spin />
      ) : (
        apiStats.absentToday
      ),
      icon: faCalendarTimes,
      color: "#e53e3e",
      type: "absentToday",
      hasViewButton: true,
    },
    {
      label: "ATTENDANCE RATE",
      value: isLoadingAbsentToday ? (
        <FontAwesomeIcon icon={faSpinner} spin />
      ) : (
        apiStats.attendanceRate
      ),
      icon: faChartLine,
      color: "#9f7aea",
      type: "attendanceRate",
    },
  ];

  // Handle API errors
  const handleApiError = (error, context = "operation") => {
    console.error(`API Error in ${context}:`, error);
    Swal.fire({
      icon: "error",
      title: "Server Error",
      text: `Failed to ${context}. Please check if the server is running.`,
      confirmButtonColor: "#e53e3e",
      footer: `<small>Error: ${error.message || "Unknown error"}</small>`,
    });
  };

  // Fetch sections by class
  const fetchAvailableSections = async () => {
    if (!classId) {
      console.error("No class ID provided");
      return;
    }

    try {
      const response = await fetch(
        `${PHP_API_BASE}?action=get-sections-by-class&className=${encodeURIComponent(
          classId
        )}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setAvailableSections(data.sections || []);
        setApiStats((prev) => ({
          ...prev,
          sectionCount: data.sections?.length || 0,
        }));
      } else {
        setAvailableSections([]);
      }
    } catch (error) {
      handleApiError(error, "fetch sections");
      setAvailableSections([]);
    }
  };

  // Fetch students with filters
  const fetchData = async (selectedSection = "All") => {
    if (!classId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Class",
        text: "Please select a class first",
        confirmButtonColor: "#667eea",
      });
      return;
    }

    try {
      setLoading(true);
      let url = `${PHP_API_BASE}?action=fetch-students&className=${encodeURIComponent(
        classId
      )}`;

      if (filter === "By Section" && selectedSection !== "All") {
        url += `&section=${encodeURIComponent(selectedSection)}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        if (filter === "By Section") {
          const groupedBySection = {};
          data.data.forEach((student) => {
            const section = student.section || "No Section";
            if (!groupedBySection[section]) {
              groupedBySection[section] = [];
            }
            groupedBySection[section].push(student);
          });

          if (selectedSection === "All") {
            setSections(groupedBySection);
            setStudents([]);
          } else {
            setSections({ [selectedSection]: data.data || [] });
            setStudents([]);
          }
        } else {
          setStudents(data.data || []);
          setSections({});
        }

        // Update stats
        updateStats(data.data || []);
        await fetchAbsentToday();
      } else {
        Swal.fire({
          icon: "warning",
          title: "No Data",
          text: data.error || "No students found for this class",
          confirmButtonColor: "#667eea",
        });
      }
    } catch (error) {
      handleApiError(error, "fetch students");
      setStudents([]);
      setSections({});
    } finally {
      setLoading(false);
    }
  };

  // Update statistics with real-time calculation
  const updateStats = (studentsData) => {
    let total = 0;
    if (filter === "By Section" && sectionFilter === "All") {
      total = Object.values(sections).reduce(
        (sum, sectionStudents) => sum + (sectionStudents?.length || 0),
        0
      );
    } else if (filter === "By Section" && sectionFilter !== "All") {
      total = sections[sectionFilter]?.length || 0;
    } else {
      total = studentsData.length;
    }

    setApiStats((prev) => ({
      ...prev,
      totalStudents: total,
      selectedCount: selectedStudents.length,
      sectionCount: availableSections.length || Object.keys(sections).length,
      attendanceRate: calculateAttendanceRate(total, absentToday),
    }));
  };

  // Handle autocomplete search
  const handleAutocompleteChange = async (input) => {
    setSearchQuery(input);

    if (input.trim() === "" || !classId) {
      setSuggestions([]);
      return;
    }

    try {
      const allStudents =
        filter === "By Section" ? Object.values(sections).flat() : students;

      const filtered = allStudents
        .filter(
          (student) =>
            student.name.toLowerCase().includes(input.toLowerCase()) ||
            student.student_id.toLowerCase().includes(input.toLowerCase())
        )
        .slice(0, 5);

      setSuggestions(filtered);
    } catch (error) {
      setSuggestions([]);
    }
  };

  // Handle search button click
  const handleSearchButtonClick = async () => {
    if (!classId) {
      Swal.fire({
        icon: "warning",
        title: "Missing Class",
        text: "Class information is required for search",
        confirmButtonColor: "#667eea",
      });
      return;
    }

    if (!searchQuery.trim()) {
      fetchData(sectionFilter);
      return;
    }

    try {
      setLoading(true);
      const allStudents = await fetchAllStudents();

      const filtered = allStudents.filter(
        (student) =>
          student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          student.father_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          student.student_id.toLowerCase().includes(searchQuery.toLowerCase())
      );

      if (filter === "By Section" && sectionFilter === "All") {
        const groupedBySection = {};
        filtered.forEach((student) => {
          const section = student.section || "No Section";
          if (!groupedBySection[section]) {
            groupedBySection[section] = [];
          }
          groupedBySection[section].push(student);
        });
        setSections(groupedBySection);
        setStudents([]);
      } else if (filter === "By Section" && sectionFilter !== "All") {
        const sectionStudents = filtered.filter(
          (student) => student.section === sectionFilter
        );
        setSections({ [sectionFilter]: sectionStudents });
        setStudents([]);
      } else {
        setStudents(filtered);
        setSections({});
      }

      updateStats(filtered);
      await fetchAbsentToday();
      setSuggestions([]);
    } catch (error) {
      handleApiError(error, "search students");
    } finally {
      setLoading(false);
    }
  };

  // Helper to fetch all students
  const fetchAllStudents = async () => {
    try {
      const response = await fetch(
        `${PHP_API_BASE}?action=fetch-students&className=${encodeURIComponent(
          classId
        )}`
      );

      if (!response.ok) throw new Error("Failed to fetch students");

      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      return [];
    }
  };

  // Check for existing absent records
  const checkExistingAbsentRecords = async (date, studentIds) => {
    try {
      const response = await fetch(
        `${PHP_API_BASE}?action=get-absent-records-by-date&date=${date}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        const existingIds = data.data
          .filter((record) => studentIds.includes(record.student_id))
          .map((record) => record.student_id);

        return {
          existing: existingIds,
          count: existingIds.length,
          allRecords: data.data,
        };
      }

      return { existing: [], count: 0, allRecords: [] };
    } catch (error) {
      return { existing: [], count: 0, allRecords: [] };
    }
  };

  // Validate selected date is not in the future
  const validateDate = (dateString) => {
    const selectedDate = new Date(dateString);
    const today = new Date();

    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      return {
        valid: false,
        message:
          "Cannot mark absent for future dates! Please select today or a past date.",
      };
    }

    return { valid: true, message: "" };
  };

  // Handle mark absent
  const handleAddAbsentClick = async () => {
    if (selectedStudents.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Students Selected",
        text: "Please select at least one student to mark as absent",
        confirmButtonColor: "#667eea",
      });
      return;
    }

    const absentStudents = [];
    const selectedStudentIds = selectedStudents;

    selectedStudentIds.forEach((studentId) => {
      const studentData = selectedStudentsData[studentId];
      if (studentData) {
        absentStudents.push({
          student_id: studentData.student_id,
          name: studentData.name,
          father_name: studentData.father_name,
          section: studentData.section,
          class: studentData.class,
          mobile: studentData.mobile,
          family_status: studentData.family_status,
        });
      }
    });

    if (absentStudents.length < selectedStudentIds.length) {
      const currentViewStudents =
        filter === "All Students"
          ? students
          : sectionFilter === "All"
          ? Object.values(sections).flat()
          : sections[sectionFilter] || [];

      selectedStudentIds.forEach((studentId) => {
        if (!absentStudents.some((s) => s.student_id === studentId)) {
          const studentInView = currentViewStudents.find(
            (s) => s.student_id === studentId
          );
          if (studentInView) {
            absentStudents.push({
              student_id: studentInView.student_id,
              name: studentInView.name,
              father_name: studentInView.father_name,
              section: studentInView.section,
              class: studentInView.class,
              mobile: studentInView.mobile,
              family_status: studentInView.family_status,
            });
            setSelectedStudentsData((prev) => ({
              ...prev,
              [studentId]: studentInView,
            }));
          }
        }
      });
    }

    if (absentStudents.length === 0) {
      Swal.fire({
        icon: "error",
        title: "No Students Found",
        text: "Could not find data for selected students. Please refresh and try again.",
        confirmButtonColor: "#667eea",
      });
      return;
    }

    const className = absentStudents[0]?.class || classId || "Unknown Class";
    const modalId = Date.now();
    const dateInputId = `attendance-date-${modalId}`;
    const dayInputId = `attendance-day-${modalId}`;
    const todayDateStr = getTodayDate();

    const { value: formValues } = await Swal.fire({
      title: "Mark Student Absence",
      html: `
      <div style="font-family: 'Inter', sans-serif;">
        <div style="margin-bottom: 1.5rem;">
          <label style="display: block; margin-bottom: 0.5rem; color: #4a5568; font-weight: 600; text-align: left;">
            Select Date (Max: ${todayDateStr})
          </label>
          <input 
            type="date" 
            id="${dateInputId}" 
            class="swal2-input" 
            value="${attendanceDate}" 
            max="${todayDateStr}"
            style="padding: 10px; border-radius: 6px; border: 1px solid #cbd5e0; width: 80%;" 
          />
        </div>

        <div style="margin-bottom: 1.5rem;">
          <label style="display: block; margin-bottom: 0.5rem; color: #4a5568; font-weight: 600; text-align: left;">
            Day (Auto-detected from date)
          </label>
          <input 
            type="text" 
            id="${dayInputId}" 
            class="swal2-input" 
            readonly
            style="padding: 10px; border-radius: 6px; border: 1px solid #cbd5e0; width: 80%; background-color: #f7fafc;" 
            value="${attendanceDay}"
          />
        </div>

        <div style="margin-top: 1.5rem; padding: 1rem; background: #f7fafc; border-radius: 8px; border-left: 4px solid #e53e3e;">
          <p style="margin: 0; color: #718096; font-size: 0.9rem;">
            <strong style="color: #e53e3e;">${
              selectedStudents.length
            }</strong> students selected, <strong style="color: #e53e3e;">${
        absentStudents.length
      }</strong> with valid data will be marked as absent in 
            <strong>${className}</strong>
          </p>
          ${
            selectedStudents.length > absentStudents.length
              ? `<p style="margin: 0.5rem 0 0 0; color: #d69e2e; font-size: 0.8rem;">
              <strong>Note:</strong> ${
                selectedStudents.length - absentStudents.length
              } selected student(s) could not be found in current view. They will be skipped.
            </p>`
              : ""
          }
          <p style="margin: 0.5rem 0 0 0; color: #718096; font-size: 0.8rem;">
            <strong>System will automatically prevent duplicate entries for the same date.</strong>
          </p>
          <p style="margin: 0.5rem 0 0 0; color: #718096; font-size: 0.8rem;">
            <strong>Date Restriction:</strong> Future dates are not allowed.
          </p>
        </div>
      </div>
    `,
      showCancelButton: true,
      confirmButtonText: "Mark Absent",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#a0aec0",
      focusConfirm: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        const dateInput = document.getElementById(dateInputId);
        const dayInput = document.getElementById(dayInputId);

        if (dateInput && dayInput) {
          dateInput.addEventListener("change", function () {
            const selectedDate = this.value;
            if (selectedDate) {
              const dayName = getDayFromDate(selectedDate);
              dayInput.value = dayName;
            }
          });
        }
      },
      preConfirm: () => {
        const dateInput = document.getElementById(dateInputId);
        const dayInput = document.getElementById(dayInputId);

        if (!dateInput || !dayInput) {
          Swal.showValidationMessage(
            "Unable to access form inputs. Please try again."
          );
          return false;
        }

        const date = dateInput.value;
        const day = dayInput.value;

        if (!date) {
          Swal.showValidationMessage("Date is required!");
          return false;
        }

        if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          Swal.showValidationMessage("Date must be in YYYY-MM-DD format!");
          return false;
        }

        const dateValidation = validateDate(date);
        if (!dateValidation.valid) {
          Swal.showValidationMessage(dateValidation.message);
          return false;
        }

        return { date, day };
      },
    });

    if (!formValues) {
      return;
    }

    const { date, day } = formValues;
    const finalDay = day;
    const studentIds = absentStudents.map((s) => s.student_id);
    let existingCheck;
    
    try {
      existingCheck = await checkExistingAbsentRecords(date, studentIds);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error Checking Existing Records",
        text: "Unable to check for existing absent records. Please try again.",
        confirmButtonColor: "#e53e3e",
      });
      return;
    }

    let finalAbsentStudents = absentStudents;
    let duplicateCount = 0;

    if (existingCheck.count > 0) {
      finalAbsentStudents = absentStudents.filter(
        (student) => !existingCheck.existing.includes(student.student_id)
      );
      duplicateCount = existingCheck.count;

      if (finalAbsentStudents.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Already Marked Absent",
          html: `
          <div style="text-align: left;">
            <p>All selected students are already marked absent for <strong>${date}</strong>.</p>
            <p>No action was taken.</p>
          </div>
        `,
          confirmButtonColor: "#667eea",
        });
        return;
      }

      const { isConfirmed } = await Swal.fire({
        title: "Duplicate Entries Found",
        html: `
        <div style="text-align: left;">
          <p><strong>${duplicateCount}</strong> out of ${absentStudents.length} students are already marked absent for ${date}.</p>
          <p>Do you want to mark only the remaining <strong>${finalAbsentStudents.length}</strong> students?</p>
        </div>
      `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Mark Remaining",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#e53e3e",
        cancelButtonColor: "#a0aec0",
      });

      if (!isConfirmed) {
        return;
      }
    }

    try {
      setIsProcessing(true);

      const formData = new FormData();
      formData.append("date", date);
      formData.append("day", finalDay);
      formData.append("absentStudents", JSON.stringify(finalAbsentStudents));
      formData.append("className", className);
      formData.append("action", "mark-absent");

      Swal.fire({
        title: "Processing...",
        text: "Marking students as absent",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await fetch(PHP_API_BASE, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      Swal.close();

      if (result.error) {
        throw new Error(result.error);
      }

      let htmlContent = `
      <div style="text-align: left; max-height: 60vh; overflow-y: auto;">
        <p style="color: #48bb78; font-weight: 600; margin-bottom: 1rem;">
          ${result.message}
        </p>
        
        <div style="background: #f7fafc; padding: 12px; border-radius: 6px; margin-bottom: 1rem; border: 1px solid #e2e8f0;">
          <p style="margin: 0 0 8px 0; font-weight: 600; color: #4a5568;">Attendance Summary:</p>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
            <div style="color: #38a169;">
              <span style="font-weight: 600;">✓ New Absences:</span> ${
                result.stats?.successful || 0
              }
            </div>
            <div style="color: #d69e2e;">
              <span style="font-weight: 600;">⚠️ Already Absent:</span> ${
                duplicateCount + (result.stats?.duplicates || 0)
              }
            </div>
            <div style="color: #e53e3e;">
              <span style="font-weight: 600;">✗ Failed:</span> ${
                result.stats?.failed || 0
              }
            </div>
            <div style="color: #4a5568;">
              <span style="font-weight: 600;">📅 Date:</span> ${date} (${finalDay})
            </div>
          </div>
        </div>
    `;

      if (selectedStudents.length > absentStudents.length) {
        const missingCount = selectedStudents.length - absentStudents.length;
        htmlContent += `
        <div style="background: #fffaf0; padding: 12px; border-radius: 6px; margin-bottom: 1rem; border: 1px solid #fed7d7;">
          <p style="margin: 0 0 8px 0; font-weight: 600; color: #c05621;">Missing Student Data:</p>
          <p style="margin: 0; color: #718096; font-size: 0.9em;">
            ${missingCount} student(s) could not be marked because their data was not available in the current view.
            This can happen when switching between filters. Try selecting them again in the current view.
          </p>
        </div>
      `;
      }

      if (
        duplicateCount > 0 ||
        (result.duplicate_students && result.duplicate_students.length > 0)
      ) {
        htmlContent += `
        <div style="background: #fffaf0; padding: 12px; border-radius: 6px; margin-bottom: 1rem; border: 1px solid #fed7d7;">
          <p style="margin: 0 0 8px 0; font-weight: 600; color: #c05621;">Duplicate Prevention:</p>
          <p style="margin: 0; color: #718096; font-size: 0.9em;">
            System prevented duplicate entries for students already marked absent.
          </p>
        </div>
      `;
      }

      if (result.errors && result.errors.length > 0) {
        htmlContent += `
        <div style="background: #fff5f5; padding: 12px; border-radius: 6px; margin-bottom: 1rem; border: 1px solid #fed7d7;">
          <p style="margin: 0 0 8px 0; font-weight: 600; color: #c53030;">Errors:</p>
            <ul style="margin: 0; padding-left: 20px; color: #718096; font-size: 0.9em;">
              ${result.errors.map((error) => `<li>${error}</li>`).join("")}
            </ul>
        </div>
      `;
      }

      htmlContent += `</div>`;

      Swal.fire({
        icon: "success",
        title: "Attendance Marked Successfully!",
        html: htmlContent,
        confirmButtonColor: "#48bb78",
        width: "600px",
        timer: 5000,
        timerProgressBar: true,
      });

      const markedStudentIds = finalAbsentStudents.map((s) => s.student_id);
      const newSelectedStudents = selectedStudents.filter(
        (id) => !markedStudentIds.includes(id)
      );
      setSelectedStudents(newSelectedStudents);

      const newSelectedStudentsData = { ...selectedStudentsData };
      markedStudentIds.forEach((id) => {
        delete newSelectedStudentsData[id];
      });
      setSelectedStudentsData(newSelectedStudentsData);

      fetchData(sectionFilter);
      await fetchAbsentToday();

      setAttendanceDate(date);
      setAttendanceDay(finalDay);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Operation Failed",
        html: `
        <div style="text-align: left;">
          <p>Failed to mark students as absent.</p>
          <p style="color: #e53e3e; font-size: 0.9em;">${error.message}</p>
        </div>
      `,
        confirmButtonColor: "#e53e3e",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle select all students
  const handleSelectAllChange = () => {
    const currentViewStudents = students;
    const currentStudentIds = currentViewStudents.map(
      (student) => student.student_id
    );
    const allCurrentSelected = currentStudentIds.every((id) =>
      selectedStudents.includes(id)
    );

    if (allCurrentSelected) {
      setSelectedStudents((prev) =>
        prev.filter((id) => !currentStudentIds.includes(id))
      );
      const newSelectedStudentsData = { ...selectedStudentsData };
      currentStudentIds.forEach((id) => {
        delete newSelectedStudentsData[id];
      });
      setSelectedStudentsData(newSelectedStudentsData);
    } else {
      const newSelections = [
        ...new Set([...selectedStudents, ...currentStudentIds]),
      ];
      setSelectedStudents(newSelections);

      const newSelectedStudentsData = { ...selectedStudentsData };
      currentViewStudents.forEach((student) => {
        newSelectedStudentsData[student.student_id] = {
          student_id: student.student_id,
          name: student.name,
          father_name: student.father_name,
          section: student.section,
          class: student.class,
          mobile: student.mobile,
          family_status: student.family_status,
        };
      });
      setSelectedStudentsData(newSelectedStudentsData);
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (student) => {
    const studentId = student.student_id;
    setSelectedStudents((prevSelectedStudents) => {
      const isSelected = prevSelectedStudents.includes(studentId);
      if (isSelected) {
        const newSelected = prevSelectedStudents.filter(
          (id) => id !== studentId
        );
        const newSelectedStudentsData = { ...selectedStudentsData };
        delete newSelectedStudentsData[studentId];
        setSelectedStudentsData(newSelectedStudentsData);
        return newSelected;
      } else {
        const newSelected = [...prevSelectedStudents, studentId];
        setSelectedStudentsData((prev) => ({
          ...prev,
          [studentId]: {
            student_id: student.student_id,
            name: student.name,
            father_name: student.father_name,
            section: student.section,
            class: student.class,
            mobile: student.mobile,
            family_status: student.family_status,
          },
        }));
        return newSelected;
      }
    });
  };

  // Handle section select all
  const handleSectionSelectAllChange = (sectionId) => {
    const sectionStudents = sections[sectionId] || [];
    const sectionStudentIds = sectionStudents.map(
      (student) => student.student_id
    );
    const allSectionSelected = sectionStudentIds.every((id) =>
      selectedStudents.includes(id)
    );

    setSelectedStudents((prevSelectedStudents) => {
      if (allSectionSelected) {
        const newSelected = prevSelectedStudents.filter(
          (id) => !sectionStudentIds.includes(id)
        );
        const newSelectedStudentsData = { ...selectedStudentsData };
        sectionStudentIds.forEach((id) => {
          delete newSelectedStudentsData[id];
        });
        setSelectedStudentsData(newSelectedStudentsData);
        return newSelected;
      } else {
        const newSelected = [
          ...new Set([...prevSelectedStudents, ...sectionStudentIds]),
        ];
        const newSelectedStudentsData = { ...selectedStudentsData };
        sectionStudents.forEach((student) => {
          newSelectedStudentsData[student.student_id] = {
            student_id: student.student_id,
            name: student.name,
            father_name: student.father_name,
            section: student.section,
            class: student.class,
            mobile: student.mobile,
            family_status: student.family_status,
          };
        });
        setSelectedStudentsData(newSelectedStudentsData);
        return newSelected;
      }
    });
  };

  // Handle outside click
  const handleOutsideClick = (event) => {
    if (
      suggestionsRef.current &&
      !suggestionsRef.current.contains(event.target)
    ) {
      setSuggestions([]);
    }
  };

  // Handle back button
  const handleBackClick = () => {
    navigate("/manage-classes");
  };

  // Handle refresh
  const handleRefreshClick = () => {
    setLoading(true);
    setSearchQuery("");
    setSuggestions([]);
    fetchData(sectionFilter);
  };

  // Clear selections
  const handleClearSelections = () => {
    Swal.fire({
      title: "Clear Selections",
      text: "Are you sure you want to clear all selected students?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#a0aec0",
      confirmButtonText: "Yes, clear all",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedStudents([]);
        setSelectedStudentsData({});
        Swal.fire({
          title: "Cleared!",
          text: "All selections have been cleared.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  // Debug database
  const handleDebugDatabase = async () => {
    try {
      const response = await fetch(`${PHP_API_BASE}?action=debug-db`);
      const data = await response.json();

      if (data.debug_info) {
        Swal.fire({
          title: "Database Debug Info",
          html: `
            <div style="text-align: left; max-height: 60vh; overflow-y: auto; font-family: monospace; font-size: 0.9em;">
              <p><strong>Database Name:</strong> ${
                data.debug_info.database_name
              }</p>
              <p><strong>Total Tables:</strong> ${
                data.debug_info.tables_in_database.length
              }</p>
              <p><strong>Available Classes:</strong> ${data.debug_info.available_classes.join(
                ", "
              )}</p>
              <hr>
              <p><strong>2k24_students Structure:</strong></p>
              <pre>${JSON.stringify(
                data.debug_info["2k24_students_structure"],
                null,
                2
              )}</pre>
              <hr>
              <p><strong>Sample Students:</strong></p>
              <pre>${JSON.stringify(data.debug_info.sample_data, null, 2)}</pre>
              ${
                data.debug_info.absent_students_structure
                  ? `
                <hr>
                <p><strong>absent_students Structure:</strong></p>
                <pre>${JSON.stringify(
                  data.debug_info.absent_students_structure,
                  null,
                  2
                )}</pre>
              `
                  : ""
              }
            </div>
          `,
          width: "80%",
          confirmButtonColor: "#667eea",
        });
      }
    } catch (error) {
      console.error("Debug error:", error);
    }
  };

  // Test mark absent API
  const testMarkAbsentAPI = async () => {
    try {
      const testData = {
        date: formatDate(new Date()),
        day: getDayFromDate(new Date()),
        absentStudents: [
          {
            student_id: "Std-PG-2501",
            name: "Test Student",
            father_name: "Test Father",
            section: "A-Boys",
            class: "Play Group",
            mobile: "0314-008-0001",
            family_status: "Family",
          },
        ],
      };

      const response = await fetch(`${PHP_API_BASE}?action=test-mark-absent`, {
        method: "GET",
      });

      const result = await response.json();

      if (result.success) {
        Swal.fire({
          icon: "success",
          title: "API Test Successful",
          text: "The mark absent API is working correctly",
          confirmButtonColor: "#48bb78",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "API Test Failed",
          html: `<div style="text-align: left;">
            <p><strong>Error:</strong> ${result.error}</p>
            <p><strong>Test Data:</strong></p>
            <pre style="background: #f7fafc; padding: 10px; border-radius: 6px; font-size: 0.8em;">
${JSON.stringify(result.test_data, null, 2)}
            </pre>
          </div>`,
          confirmButtonColor: "#e53e3e",
        });
      }

      return result;
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "API Test Error",
        text: error.message,
        confirmButtonColor: "#e53e3e",
      });
      return { error: error.message };
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setFilter(newFilter);

    if (newFilter === "By Section") {
      fetchAvailableSections();
    }

    setStudents([]);
    setSections({});
    setSearchQuery("");
    setSuggestions([]);
  };

  // Handle section filter change
  const handleSectionFilterChange = (e) => {
    const newSection = e.target.value;
    setSectionFilter(newSection);
    fetchData(newSection);
  };

  // Handle view today's absent students
  const handleViewAbsentToday = () => {
    if (absentTodayList.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Absent Students Today",
        text: `There are no absent students for ${attendanceDate} (${attendanceDay})`,
        confirmButtonColor: "#667eea",
      });
      return;
    }
    setShowAbsentModal(true);
  };

// Mark student as present
const handleMarkPresent = async (recordId, studentId) => {
  try {
    setMarkingPresent(prev => ({ ...prev, [recordId]: true }));
    
    const result = await Swal.fire({
      title: 'Mark Student as Present?',
      text: 'This will remove the student from today\'s absent list.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#48bb78',
      cancelButtonColor: '#e53e3e',
      confirmButtonText: 'Yes, mark as present',
      cancelButtonText: 'Cancel'
    });
    
    if (!result.isConfirmed) {
      setMarkingPresent(prev => ({ ...prev, [recordId]: false }));
      return;
    }
    
    const formData = new FormData();
    formData.append('recordId', recordId);
    formData.append('studentId', studentId);
    formData.append('date', getTodayDate());
    formData.append('className', classId); // ADD THIS LINE
    formData.append('action', 'delete-absent-record');
    
    console.log('Deleting absent record:', {
      recordId,
      studentId,
      date: getTodayDate(),
      className: classId
    });
    
    const response = await fetch(PHP_API_BASE, {
      method: 'POST',
      body: formData
    });
    
    const data = await response.json();
    console.log('Delete response:', data);
    
    if (data.success) {
      // Remove the record from the list
      setAbsentTodayList(prev => prev.filter(record => 
        record.id !== recordId && record.student_id !== studentId
      ));
      
      // Update absent count
      const newAbsentCount = absentToday - 1;
      setAbsentToday(newAbsentCount);
      
      // Recalculate attendance rate
      const totalStudents = apiStats.totalStudents;
      const attendanceRate = calculateAttendanceRate(totalStudents, newAbsentCount);
      
      setApiStats(prev => ({
        ...prev,
        absentToday: newAbsentCount,
        attendanceRate: attendanceRate
      }));
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Student has been marked as present.',
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      // Show more detailed error message
      throw new Error(data.error || 'Failed to mark as present. ' + 
        (data.search_criteria ? `Criteria: ${JSON.stringify(data.search_criteria)}` : ''));
    }
  } catch (error) {
    console.error('Error marking present:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      html: `
        <div style="text-align: left;">
          <p>${error.message}</p>
          ${error.message.includes('Criteria') ? 
            `<small style="color: #718096;">Check if the record exists in the database.</small>` : ''}
        </div>
      `,
      confirmButtonColor: '#e53e3e',
    });
  } finally {
    setMarkingPresent(prev => ({ ...prev, [recordId]: false }));
  }
};

  // Refresh modal data
  const handleRefreshModalData = async () => {
    try {
      setModalLoading(true);
      await fetchAbsentToday();
      Swal.fire({
        icon: 'success',
        title: 'Refreshed!',
        text: 'Absent list has been updated.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to refresh data',
        confirmButtonColor: '#e53e3e',
      });
    } finally {
      setModalLoading(false);
    }
  };

  // Initialize component
  useEffect(() => {
    if (classId) {
      fetchData(sectionFilter);
      if (filter === "By Section") {
        fetchAvailableSections();
      }
      fetchAbsentToday();
    } else {
      Swal.fire({
        icon: "warning",
        title: "No Class Selected",
        text: "Please select a class from the Classes Management page",
        confirmButtonColor: "#667eea",
      }).then(() => {
        navigate("/manage-classes");
      });
    }

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [classId, filter]);

  // Update selected count in stats
  useEffect(() => {
    updateStats(
      filter === "All Students" ? students : Object.values(sections).flat()
    );
  }, [selectedStudents, students, sections, filter, sectionFilter, absentToday]);

  // Check if all students in current view are selected
  const areAllInViewSelected = () => {
    if (filter === "All Students") {
      return (
        students.length > 0 &&
        students.every((student) =>
          selectedStudents.includes(student.student_id)
        )
      );
    } else if (filter === "By Section") {
      if (sectionFilter === "All") {
        return Object.values(sections)
          .flat()
          .every((student) => selectedStudents.includes(student.student_id));
      } else {
        return (sections[sectionFilter] || []).every((student) =>
          selectedStudents.includes(student.student_id)
        );
      }
    }
    return false;
  };

  // Absent Today Modal Component
  const AbsentTodayModal = () => {
    if (!showAbsentModal) return null;
    
    // Group absent students by section
    const groupedBySection = {};
    absentTodayList.forEach((student) => {
      const section = student.section || 'No Section';
      if (!groupedBySection[section]) {
        groupedBySection[section] = [];
      }
      groupedBySection[section].push(student);
    });

    return ReactDOM.createPortal(
      <div className="class-attendance-ck-modal-overlay" onClick={() => setShowAbsentModal(false)}>
        <div className="class-attendance-ck-modal" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="class-attendance-ck-modal-header">
            <h3 className="class-attendance-ck-modal-title">
              <FontAwesomeIcon icon={faCalendarTimes} />
              Today's Absent Students
            </h3>
            <button 
              className="class-attendance-ck-modal-close"
              onClick={() => setShowAbsentModal(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          {/* Modal Content */}
          <div className="class-attendance-ck-modal-content">
            <div className="class-attendance-ck-modal-table-container">
              {Object.entries(groupedBySection).map(([section, students]) => (
                <div key={section} className="class-attendance-ck-modal-section">
                  <div className="class-attendance-ck-modal-section-header">
                    <h4 className="class-attendance-ck-modal-section-title">
                      {section === '' ? 'No Section' : `Section: ${section}`}
                      <span className="class-attendance-ck-modal-section-count">
                        {students.length} student{students.length !== 1 ? 's' : ''}
                      </span>
                    </h4>
                  </div>
                  
                  <table className="class-attendance-ck-modal-table">
                    <thead>
                      <tr>
                        <th>Student ID</th>
                        <th>Name</th>
                        <th>Father's Name</th>
                        <th>Class</th>
                        <th>Section</th>
                        <th>Mobile</th>
                        <th>Family Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id || student.student_id}>
                          <td>
                            <span className="class-attendance-ck-student-id-badge">
                              {student.student_id}
                            </span>
                          </td>
                          <td>
                            <div className="class-attendance-ck-student-name">
                              {student.name}
                            </div>
                          </td>
                          <td>{student.father_name || '-'}</td>
                          <td>
                            <span className="class-attendance-ck-section-badge">
                              {student.class}
                            </span>
                          </td>
                          <td>
                            <span className="class-attendance-ck-section-badge">
                              {student.section || 'No Section'}
                            </span>
                          </td>
                          <td>{student.mobile || '-'}</td>
                          <td>
                            <span className={`class-attendance-ck-family-status ${student.family_status?.toLowerCase() || 'unknown'}`}>
                              {student.family_status || 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="class-attendance-ck-mark-present-btn"
                              onClick={() => handleMarkPresent(student.id, student.student_id)}
                              disabled={markingPresent[student.id]}
                            >
                              <FontAwesomeIcon icon={markingPresent[student.id] ? faSpinner : faCheck} 
                                spin={markingPresent[student.id]} />
                              {markingPresent[student.id] ? 'Processing...' : 'Mark Present'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="class-attendance-ck-modal-footer">
            <div className="class-attendance-ck-modal-summary">
              <div className="class-attendance-ck-summary-item">
                <span className="class-attendance-ck-summary-label">Total Absent:</span>
                <span className="class-attendance-ck-summary-value">{absentToday} students</span>
              </div>
              <div className="class-attendance-ck-summary-item">
                <span className="class-attendance-ck-summary-label">Date:</span>
                <span className="class-attendance-ck-summary-value">{attendanceDate} ({attendanceDay})</span>
              </div>
              <div className="class-attendance-ck-summary-item">
                <span className="class-attendance-ck-summary-label">Class:</span>
                <span className="class-attendance-ck-summary-value">{classId}</span>
              </div>
            </div>
            
            <div className="class-attendance-ck-modal-actions">
              <button
                className="class-attendance-ck-modal-btn class-attendance-ck-modal-btn-secondary"
                onClick={handleRefreshModalData}
                disabled={modalLoading}
              >
                <FontAwesomeIcon icon={modalLoading ? faSpinner : faRefresh} spin={modalLoading} />
                {modalLoading ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                className="class-attendance-ck-modal-btn class-attendance-ck-modal-btn-primary"
                onClick={() => setShowAbsentModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <main className="main-content">
        {/* Header Section */}
        <div className="class-attendance-ck-header">
          <div className="class-attendance-ck-title-section">
            <h2>
              <FontAwesomeIcon icon={faCalendarCheck} />
              Attendance Management
            </h2>
            <p>
              Managing attendance for{" "}
              <strong className="class-attendance-ck-class-highlight">
                {classId || "Unknown Class"}
              </strong>{" "}
              •{" "}
              <span className="class-attendance-ck-selected-count-badge">
                {selectedStudents.length}
              </span>{" "}
              students selected
            </p>
            <div className="class-attendance-ck-date-info">
              <span>
                <FontAwesomeIcon icon={faCalendarAlt} /> Today: {attendanceDate}{" "}
                ({attendanceDay})
              </span>
            </div>
          </div>
          <div className="class-attendance-ck-header-actions">
            {/* Toggle switch for details */}
            <label className="class-attendance-ck-toggle-container">
              <span className="class-attendance-ck-toggle-label">
                {showDetails ? "Hide Details" : "Show Details"}
              </span>
              <input
                type="checkbox"
                checked={showDetails}
                onChange={() => setShowDetails(!showDetails)}
                className="class-attendance-ck-toggle-input"
              />
              <span className="class-attendance-ck-toggle-slider"></span>
            </label>

            <button
              className="class-attendance-ck-btn class-attendance-ck-btn-primary"
              onClick={handleBackClick}
              disabled={isProcessing}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Classes
            </button>
            <button
              className="class-attendance-ck-btn class-attendance-ck-btn-secondary"
              onClick={handleDebugDatabase}
              title="Debug Database"
              disabled={isProcessing}
            >
              <FontAwesomeIcon icon={faDatabase} />
              Debug
            </button>
            <button
              className="class-attendance-ck-btn class-attendance-ck-btn-warning"
              onClick={testMarkAbsentAPI}
              title="Test Mark Absent API"
              disabled={isProcessing}
            >
              <FontAwesomeIcon icon={faBug} />
              Test API
            </button>
            {selectedStudents.length > 0 && (
              <button
                className="class-attendance-ck-btn class-attendance-ck-btn-danger"
                onClick={handleClearSelections}
                disabled={isProcessing}
              >
                <FontAwesomeIcon icon={faTimes} />
                Clear Selections ({selectedStudents.length})
              </button>
            )}
          </div>
        </div>

        {/* Stats Overview - hidden by default */}
        {showDetails && (
          <div className="class-attendance-ck-stats-overview">
            {stats.map((stat, index) => (
              <div
                className={`class-attendance-ck-stat-card ${
                  stat.type === "absentToday" ? "class-attendance-ck-stat-hoverable" : ""
                }`}
                key={index}
                style={{ borderLeftColor: stat.color }}
                onMouseEnter={(e) => {
                  if (stat.type === "absentToday") {
                    e.currentTarget.classList.add("class-attendance-ck-stat-hovered");
                  }
                }}
                onMouseLeave={(e) => {
                  if (stat.type === "absentToday") {
                    e.currentTarget.classList.remove("class-attendance-ck-stat-hovered");
                  }
                }}
              >
                <div className="class-attendance-ck-stat-icon" style={{ color: stat.color }}>
                  <FontAwesomeIcon icon={stat.icon} />
                </div>
                <div className="class-attendance-ck-stat-value">{stat.value}</div>
                <div className="class-attendance-ck-stat-label" style={{ color: stat.color }}>
                  {stat.label}
                </div>
                
                {/* View Button for Absent Today - Only shows on hover */}
                {stat.type === "absentToday" && (
                  <button
                    className="class-attendance-ck-stat-view-btn"
                    onClick={handleViewAbsentToday}
                    title="View Today's Absent Students"
                  >
                    <FontAwesomeIcon icon={faEye} />
                    <span>View</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Control Panel (filters & search) - hidden by default */}
        {showDetails && (
          <div className="class-attendance-ck-control-panel">
            <div className="class-attendance-ck-filter-section">
              <div className="class-attendance-ck-filter-group">
                <label className="class-attendance-ck-filter-label">
                  <FontAwesomeIcon icon={faFilter} /> View By:
                </label>
                <select
                  className="class-attendance-ck-filter-select"
                  value={filter}
                  onChange={handleFilterChange}
                  disabled={loading || isProcessing}
                >
                  <option value="All Students">All Students</option>
                  <option value="By Section">By Section</option>
                </select>
              </div>

              {filter === "By Section" && (
                <div className="class-attendance-ck-filter-group">
                  <label className="class-attendance-ck-filter-label">Section:</label>
                  <select
                    className="class-attendance-ck-filter-select"
                    value={sectionFilter}
                    onChange={handleSectionFilterChange}
                    disabled={
                      loading || availableSections.length === 0 || isProcessing
                    }
                  >
                    <option value="All">All Sections</option>
                    {availableSections.map((section, index) => (
                      <option key={index} value={section}>
                        {section}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="class-attendance-ck-search-section">
              <div className="class-attendance-ck-search-wrapper">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="class-attendance-ck-search-icon"
                />
                <input
                  type="text"
                  className="class-attendance-ck-search-input"
                  placeholder="Search by name or student ID..."
                  value={searchQuery}
                  onChange={(e) => handleAutocompleteChange(e.target.value)}
                  disabled={loading || isProcessing}
                  onKeyPress={(e) =>
                    e.key === "Enter" && handleSearchButtonClick()
                  }
                />

                {suggestions.length > 0 && (
                  <ul ref={suggestionsRef} className="class-attendance-ck-suggestion-list">
                    {suggestions.map((student, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          setSearchQuery(student.name);
                          setSuggestions([]);
                        }}
                        className="class-attendance-ck-suggestion-item"
                      >
                        <div className="class-attendance-ck-suggestion-name">{student.name}</div>
                        <div className="class-attendance-ck-suggestion-details">
                          {student.student_id} • {student.father_name}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="class-attendance-ck-action-buttons">
                <button
                  className="class-attendance-ck-btn class-attendance-ck-btn-primary"
                  onClick={handleSearchButtonClick}
                  disabled={loading || isProcessing}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  {loading ? "Searching..." : "Search"}
                </button>
                <button
                  className="class-attendance-ck-btn class-attendance-ck-btn-secondary"
                  onClick={handleRefreshClick}
                  disabled={loading || isProcessing}
                >
                  <FontAwesomeIcon icon={faRefresh} spin={loading} />
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
                <button
                  className="class-attendance-ck-btn class-attendance-ck-btn-warning"
                  onClick={handleAddAbsentClick}
                  disabled={
                    selectedStudents.length === 0 || loading || isProcessing
                  }
                >
                  <FontAwesomeIcon icon={faUserMinus} />
                  Mark Absent ({selectedStudents.length})
                </button>
                <div className="class-attendance-ck-selected-indicator">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="class-attendance-ck-selected-icon"
                  />
                  <span className="class-attendance-ck-selected-text">
                    {selectedStudents.length} Selected
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {(loading || isProcessing) && (
          <div className="class-attendance-ck-loading-overlay">
            <div className="class-attendance-ck-loading-spinner">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>
                {isProcessing
                  ? "Processing attendance..."
                  : "Loading student data..."}
              </p>
            </div>
          </div>
        )}

        {/* Students Table */}
        <div className="class-attendance-ck-table-container">
          <div className="class-attendance-ck-table-header">
            <h3>
              <FontAwesomeIcon icon={faUsers} />
              {filter === "All Students"
                ? `All Students in ${classId}`
                : sectionFilter === "All"
                ? `Students by Section in ${classId}`
                : `Students in Section ${sectionFilter}`}
              {!loading && (
                <span className="class-attendance-ck-table-count">
                  (
                  {filter === "All Students"
                    ? students.length
                    : sectionFilter === "All"
                    ? Object.values(sections).reduce(
                        (sum, arr) => sum + arr.length,
                        0
                      )
                    : sections[sectionFilter]?.length || 0}{" "}
                  students)
                </span>
              )}
            </h3>
            {students.length > 0 && filter === "All Students" && !loading && (
              <div className="class-attendance-ck-select-all-container">
                <input
                  type="checkbox"
                  className="class-attendance-ck-table-checkbox"
                  onChange={handleSelectAllChange}
                  checked={areAllInViewSelected()}
                  disabled={loading || isProcessing}
                />
                <span className="class-attendance-ck-select-all-text">Select All in View</span>
              </div>
            )}
          </div>

          {!loading && filter === "All Students" ? (
            students.length > 0 ? (
              <div className="class-attendance-ck-table-responsive">
                <table className="class-attendance-ck-attendance-table">
                  <thead>
                    <tr>
                      <th className="class-attendance-ck-checkbox-column">Mark</th>
                      <th className="class-attendance-ck-col-id">Student ID</th>
                      <th className="class-attendance-ck-col-name">Name</th>
                      <th className="class-attendance-ck-col-father">Father's Name</th>
                      <th className="class-attendance-ck-col-section">Section</th>
                      <th className="class-attendance-ck-col-class">Class</th>
                      <th className="class-attendance-ck-col-mobile">Mobile</th>
                      <th className="class-attendance-ck-col-status">Family Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr
                        key={student.student_id}
                        className={
                          selectedStudents.includes(student.student_id)
                            ? "class-attendance-ck-row-selected"
                            : ""
                        }
                      >
                        <td className="class-attendance-ck-checkbox-column">
                          <input
                            type="checkbox"
                            className="class-attendance-ck-table-checkbox"
                            checked={selectedStudents.includes(
                              student.student_id
                            )}
                            onChange={() => handleCheckboxChange(student)}
                            disabled={loading || isProcessing}
                          />
                        </td>
                        <td className="class-attendance-ck-col-id">
                          <span className="class-attendance-ck-student-id-badge">
                            {student.student_id}
                          </span>
                        </td>
                        <td className="class-attendance-ck-col-name">
                          <div className="class-attendance-ck-student-name">{student.name}</div>
                        </td>
                        <td className="class-attendance-ck-col-father">{student.father_name}</td>
                        <td className="class-attendance-ck-col-section">
                          <span
                            className={`class-attendance-ck-section-badge ${student.section
                              ?.replace(/\s+/g, "-")
                              .toLowerCase()}`}
                          >
                            {student.section}
                          </span>
                        </td>
                        <td className="class-attendance-ck-col-class">{student.class}</td>
                        <td className="class-attendance-ck-col-mobile">{student.mobile}</td>
                        <td className="class-attendance-ck-col-status">
                          <span
                            className={`class-attendance-ck-family-status ${student.family_status?.toLowerCase()}`}
                            style={{ color: "black" }}
                          >
                            {student.family_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="class-attendance-ck-empty-state">
                <FontAwesomeIcon icon={faUsers} className="class-attendance-ck-empty-state-icon" />
                <h3>No Students Found</h3>
                <p>
                  {searchQuery
                    ? `No students found matching "${searchQuery}"`
                    : "No students available for this class"}
                </p>
                <button
                  className="class-attendance-ck-btn class-attendance-ck-btn-secondary"
                  onClick={handleRefreshClick}
                  disabled={loading || isProcessing}
                >
                  <FontAwesomeIcon icon={faRefresh} />
                  Refresh List
                </button>
              </div>
            )
          ) : !loading && filter === "By Section" ? (
            Object.keys(sections).length > 0 ? (
              <div className="class-attendance-ck-sections-container">
                {Object.entries(sections).map(
                  ([sectionId, sectionStudents]) => (
                    <div key={sectionId} className="class-attendance-ck-section-group">
                      <div className="class-attendance-ck-section-header">
                        <h3 className="class-attendance-ck-section-title">
                          <FontAwesomeIcon icon={faBookOpen} />
                          Section: {sectionId}
                          <span className="class-attendance-ck-section-count">
                            ({sectionStudents?.length || 0} students)
                          </span>
                        </h3>
                        {sectionStudents?.length > 0 && !loading && (
                          <div className="class-attendance-ck-section-select-all">
                            <input
                              type="checkbox"
                              className="class-attendance-ck-table-checkbox"
                              onChange={() =>
                                handleSectionSelectAllChange(sectionId)
                              }
                              checked={
                                sectionStudents?.every((student) =>
                                  selectedStudents.includes(student.student_id)
                                ) && sectionStudents?.length > 0
                              }
                              disabled={loading || isProcessing}
                            />
                            <span>Select All in Section {sectionId}</span>
                          </div>
                        )}
                      </div>
                      {sectionStudents?.length > 0 ? (
                        <div className="class-attendance-ck-section-table-wrapper">
                          <table className="class-attendance-ck-attendance-table">
                            <thead>
                              <tr>
                                <th className="class-attendance-ck-checkbox-column"></th>
                                <th className="class-attendance-ck-col-id">Student ID</th>
                                <th className="class-attendance-ck-col-name">Name</th>
                                <th className="class-attendance-ck-col-father">Father's Name</th>
                                <th className="class-attendance-ck-col-section">Section</th>
                                <th className="class-attendance-ck-col-class">Class</th>
                                <th className="class-attendance-ck-col-mobile">Mobile</th>
                                <th className="class-attendance-ck-col-status">Family Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sectionStudents.map((student) => (
                                <tr
                                  key={student.student_id}
                                  className={
                                    selectedStudents.includes(
                                      student.student_id
                                    )
                                      ? "class-attendance-ck-row-selected"
                                      : ""
                                  }
                                >
                                  <td className="class-attendance-ck-checkbox-column">
                                    <input
                                      type="checkbox"
                                      className="class-attendance-ck-table-checkbox"
                                      checked={selectedStudents.includes(
                                        student.student_id
                                      )}
                                      onChange={() =>
                                        handleCheckboxChange(student)
                                      }
                                      disabled={loading || isProcessing}
                                    />
                                  </td>
                                  <td className="class-attendance-ck-col-id">
                                    <span className="class-attendance-ck-student-id-badge">
                                      {student.student_id}
                                    </span>
                                  </td>
                                  <td className="class-attendance-ck-col-name">
                                    <div className="class-attendance-ck-student-name">
                                      {student.name}
                                    </div>
                                  </td>
                                  <td className="class-attendance-ck-col-father">
                                    {student.father_name}
                                  </td>
                                  <td className="class-attendance-ck-col-section">
                                    <span className="class-attendance-ck-section-badge">
                                      {student.section}
                                    </span>
                                  </td>
                                  <td className="class-attendance-ck-col-class">
                                    {student.class}
                                  </td>
                                  <td className="class-attendance-ck-col-mobile">
                                    {student.mobile}
                                  </td>
                                  <td className="class-attendance-ck-col-status">
                                    <span
                                      className={`class-attendance-ck-family-status ${student.family_status?.toLowerCase()}`}
                                    >
                                      {student.family_status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="class-attendance-ck-empty-section">
                          No students in this section
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="class-attendance-ck-empty-state">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="class-attendance-ck-empty-state-icon"
                />
                <h3>No Sections Found</h3>
                <p>No sections available for this class</p>
              </div>
            )
          ) : null}

          {/* Footer Info */}
          {!loading && (
            <div className="class-attendance-ck-table-footer">
              <p className="class-attendance-ck-footer-text">
                <FontAwesomeIcon icon={faCalendarDay} /> Attendance Management
                System •<strong> {classId}</strong> •
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <p className="class-attendance-ck-footer-hint">
                <strong>Tip:</strong> When switching filters, make sure to
                refresh the view to see all students.
                <br />
                <strong>Note:</strong> To mark all selected students, ensure
                they are visible in the current view.
                <br />
                <small>
                  Future dates are not allowed for attendance marking.
                </small>
              </p>
            </div>
          )}
        </div>

        {/* Absent Today Modal */}
        <AbsentTodayModal />
      </main>
    </div>
  );
};

export default ClassAttendanceCK;